import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMenu } from "../../../contexts/MenuContext";
import { SelectedMenuOptions } from "../../../components/layout/Layout";
import { Card, Row, Col, Typography, Statistic } from "antd";
import {
  ShoppingCartOutlined,
  WalletOutlined,
  DollarCircleOutlined,
  FormOutlined,
  SettingOutlined,
} from "@ant-design/icons";

import styles from "./HomeDashboard.module.scss";
import { MENU_VISIBILITY_CONFIG } from "../../../config/menuVisibility";

const { Title, Text } = Typography;

interface HomeDashboardProps {
  isSuperAdmin: boolean;
  organizationsQuantity: number;
}

const HomeDashboard = ({
  isSuperAdmin,
  organizationsQuantity,
}: HomeDashboardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSelectedMenu } = useMenu();

  const modules = [
    {
      key: "admin",
      title: t("NavBar-Admin"),
      description: t("Home-Module-Admin-Description"),
      icon: <SettingOutlined />,
      path: "/admin/businessrules",
      color: "#722ed1",
      available: MENU_VISIBILITY_CONFIG.modules.admin,
    },
    {
      key: "pricings",
      title: t("NavBar-Pricings"),
      description: t("Home-Module-Pricing-Description"),
      icon: <DollarCircleOutlined />,
      path: "/pricings/dashboard",
      color: "#722ed1",
      available: MENU_VISIBILITY_CONFIG.modules.pricings,
    },
    {
      key: "promotions",
      title: t("NavBar-CommercialIntelligence"),
      description: t("Home-Module-Promotions-Description"),
      icon: <WalletOutlined />,
      path: "/commercial-analysis/summary",
      color: "#722ed1",
      available: MENU_VISIBILITY_CONFIG.modules.promotions,
    },
    {
      key: "fidelity",
      title: t("NavBar-Fidelity"),
      description: t("Home-Module-Fidelity-Description"),
      icon: <WalletOutlined />,
      path: "/fidelity/dashboard",
      color: "#722ed1",
      available: MENU_VISIBILITY_CONFIG.modules.fidelity,
    },
    {
      key: "purchaseorders",
      title: t("NavBar-Shopping"),
      description: t("Home-Module-PurchaseOrders-Description"),
      icon: <ShoppingCartOutlined />,
      path: "/purchaseorders/dashboard",
      color: "#722ed1",
      available: MENU_VISIBILITY_CONFIG.modules.purchaseOrders,
    },
    {
      key: "repositionorders",
      title: t("NavBar-Surtido"),
      description: t("Home-Module-RepositionOrders-Description"),
      icon: <FormOutlined />,
      path: "/repositionorders/dashboard",
      color: "#722ed1",
      available: MENU_VISIBILITY_CONFIG.modules.repositionOrders,
    },
  ].filter((module) => module.available);

  const handleModuleClick = (path: string, menuKey: string) => {
    setSelectedMenu(menuKey as SelectedMenuOptions);
    navigate(path);
  };

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.welcomeSection}>
          <div className={styles.welcomeText}>
            <Title level={1} className={styles.welcomeTitle}>
              {t("Home-Welcome")}!
            </Title>
            <Text className={styles.welcomeSubtitle}>
              Selecciona un módulo para comenzar
            </Text>
          </div>
        </div>

        {isSuperAdmin && (
          <Card className={styles.adminCard}>
            <Statistic
              title={t("Home-Admin-Organizations")}
              value={organizationsQuantity}
              prefix={<SettingOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        )}
      </div>

      {/* Modules Grid */}
      <div className={styles.modulesSection}>
        <Row gutter={[16, 16]} className={styles.modulesGrid}>
          {modules.map((module) => (
            <Col xs={12} sm={12} md={8} lg={6} key={module.key}>
              <Card
                className={`${styles.moduleCard} ${
                  !module.available ? styles.disabled : ""
                }`}
                hoverable={module.available}
                onClick={() =>
                  module.available && handleModuleClick(module.path, module.key)
                }
              >
                <div className={styles.moduleContent}>
                  <div
                    className={styles.moduleIcon}
                    style={{
                      color: module.available ? module.color : "#d9d9d9",
                    }}
                  >
                    {module.icon}
                  </div>
                  <Title level={4} className={styles.moduleTitle}>
                    {module.title}
                  </Title>
                  <Text className={styles.moduleDescription}>
                    {module.description}
                  </Text>
                  {!module.available && (
                    <div className={styles.comingSoon}>
                      {t("Home-ComingSoon")}
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

export default HomeDashboard;
